import React from "react";
import { css, cx } from "@emotion/css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { useUnit } from "../providers/unit.provider";
import { useAuth } from "../providers/auth.provider";
import { useCheckout } from "../hooks/use-checkout";

const overlayCss = css`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const cardCss = css`
  position: relative;
  background: #1e1e2e;
  padding: 32px 40px;
  border-radius: 12px;
  text-align: center;
  max-width: 420px;
  width: 100%;
  color: #e2e8f0;
`;

const closeBtnCss = css`
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.4em;
  cursor: pointer;
  line-height: 1;
  &:hover {
    color: #e2e8f0;
  }
`;

const lockIconCss = css`
  font-size: 2.5em;
  color: #f59e0b;
  margin-bottom: 12px;
`;

const headingCss = css`
  font-size: 1.4em;
  font-weight: bold;
  margin: 0 0 8px;
  color: #f8fafc;
`;

const subtextCss = css`
  font-size: 0.95em;
  color: #94a3b8;
  margin: 0 0 24px;
`;

const buttonGroupCss = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const baseButtonCss = css`
  border: none;
  border-radius: 6px;
  padding: 11px 20px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
  transition: opacity 0.2s;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const blueButtonCss = css`
  background: #4f9eff;
  &:hover:not(:disabled) {
    background: #3b8fe8;
  }
`;

const greenButtonCss = css`
  background: #22c55e;
  &:hover:not(:disabled) {
    background: #16a34a;
  }
`;

const amberButtonCss = css`
  background: #f59e0b;
  &:hover:not(:disabled) {
    background: #d97706;
  }
`;

const signInMessageCss = css`
  font-size: 0.95em;
  color: #94a3b8;
  margin-bottom: 16px;
`;

const errorCss = css`
  color: #f87171;
  font-size: 0.85em;
  margin-top: 8px;
`;

const refreshNoteCss = css`
  font-size: 0.78em;
  color: #64748b;
  margin-top: 16px;
`;

type Props = { onDismiss: () => void }

export const PaywallOverlay = ({ onDismiss }: Props) => {
  const { unitSlug } = useUnit();
  const { isAuthenticated } = useAuth();
  const { startCheckout, loading, error } = useCheckout();

  return (
    <div className={overlayCss}>
      <div className={cardCss}>
        <button className={closeBtnCss} onClick={onDismiss} aria-label="Close">×</button>
        <div className={lockIconCss}>
          <FontAwesomeIcon icon={faLock} />
        </div>
        <h2 className={headingCss}>Premium Unit</h2>
        <p className={subtextCss}>
          Unlock this unit or get access to everything
        </p>

        {isAuthenticated ? (
          <>
            <div className={buttonGroupCss}>
              <button
                className={cx(baseButtonCss, blueButtonCss)}
                disabled={loading}
                onClick={() => startCheckout("unit", unitSlug)}
              >
                Unlock this unit — $1
              </button>
              <button
                className={cx(baseButtonCss, greenButtonCss)}
                disabled={loading}
                onClick={() => startCheckout("unlock_all")}
              >
                ⭐ Unlock everything — $10
              </button>
              <button
                className={cx(baseButtonCss, amberButtonCss)}
                disabled={loading}
                onClick={() => startCheckout("progress_saving")}
              >
                Save progress too — $3
              </button>
            </div>
            {error && <div className={errorCss}>{error}</div>}
          </>
        ) : (
          <p className={signInMessageCss}>Sign in to unlock</p>
        )}

        <p className={refreshNoteCss}>
          Just purchased? Refresh the page to continue.
        </p>
      </div>
    </div>
  );
};
